import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Divider,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import RefreshIcon from '@mui/icons-material/Refresh'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { supabase } from '../../config/supabase'
import type { ServicesTemplateStructure } from '../../types'

interface PendingSubmission {
  id: string
  token: string
  undertaker_id?: string
  chain_id?: string
  entity_name: string
  entity_type: 'chain' | 'undertaker'
  entity_email?: string
  entity_city?: string
  submitted_at: string
  submitted_services_data: any
  submitted_profile_data: any
  template_name: string
  template_structure?: ServicesTemplateStructure
  submission_type: 'complete' | 'partial' | 'none'
  token_created_at: string
}

interface EntityCurrentData {
  services_data?: any
  profile_data?: any
}

// Profile field labels for display
const PROFILE_FIELD_LABELS: Record<string, string> = {
  phone: 'Telefon',
  email: 'E-mail',
  website: 'Website',
  cvr_number: 'CVR-nummer',
  contact_person_name: 'Kontaktperson (navn)',
  contact_person_email: 'Kontaktperson (e-mail)',
  facebook_link: 'Facebook',
  google_business_link: 'Google Business',
  instagram: 'Instagram',
  trustpilot: 'Trustpilot',
  usp1: 'USP 1',
  usp2: 'USP 2',
  usp3: 'USP 3',
  description: 'Beskrivelse',
}

export default function SubmissionReviews() {
  const [submissions, setSubmissions] = useState<PendingSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false)
  const [selectedSubmission, setSelectedSubmission] = useState<PendingSubmission | null>(null)
  const [currentData, setCurrentData] = useState<EntityCurrentData | null>(null)
  const [loadingCurrentData, setLoadingCurrentData] = useState(false)

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  // Processing
  const [processing, setProcessing] = useState(false)

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('pending_submission_reviews')
        .select('*')

      if (fetchError) throw fetchError

      setSubmissions(data || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSubmissions()
  }, [fetchSubmissions])

  const handleOpenReview = async (submission: PendingSubmission) => {
    setSelectedSubmission(submission)
    setReviewDialogOpen(true)
    setLoadingCurrentData(true)

    try {
      // Fetch current entity data for comparison
      const { data, error } = await supabase.rpc('get_entity_current_data', {
        p_undertaker_id: submission.undertaker_id || null,
        p_chain_id: submission.chain_id || null,
      })

      if (error) throw error
      setCurrentData(data)
    } catch (err: any) {
      setError(`Kunne ikke hente nuværende data: ${err.message}`)
    } finally {
      setLoadingCurrentData(false)
    }
  }

  const handleApprove = async () => {
    if (!selectedSubmission) return

    try {
      setProcessing(true)
      setError(null)

      const { data, error } = await supabase.rpc('approve_submission', {
        p_token_id: selectedSubmission.id
      })

      if (error) throw error
      if (data && !data.success) {
        throw new Error(data.error || 'Godkendelse fejlede')
      }

      setSuccess(`✅ Indsendelsen fra ${selectedSubmission.entity_name} er godkendt og data er opdateret!`)
      setReviewDialogOpen(false)
      setSelectedSubmission(null)
      setCurrentData(null)
      
      // Refresh list
      await fetchSubmissions()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleOpenReject = () => {
    setRejectReason('')
    setRejectDialogOpen(true)
  }

  const handleReject = async () => {
    if (!selectedSubmission) return

    try {
      setProcessing(true)
      setError(null)

      const { data, error } = await supabase.rpc('reject_submission', {
        p_token_id: selectedSubmission.id,
        p_reason: rejectReason || null
      })

      if (error) throw error
      if (data && !data.success) {
        throw new Error(data.error || 'Afvisning fejlede')
      }

      setSuccess(`Indsendelsen fra ${selectedSubmission.entity_name} er afvist.`)
      setRejectDialogOpen(false)
      setReviewDialogOpen(false)
      setSelectedSubmission(null)
      setCurrentData(null)
      
      // Refresh list
      await fetchSubmissions()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setProcessing(false)
    }
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Render value for display (handle different types)
  const renderValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '(tom)'
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nej'
    if (Array.isArray(value)) return value.join(', ') || '(tom)'
    if (typeof value === 'object') {
      // For nested objects like serviceList data
      const entries = Object.entries(value).filter(([_, v]) => v !== '' && v !== null)
      if (entries.length === 0) return '(tom)'
      return entries.map(([k, v]) => `${k}: ${v}`).join(', ')
    }
    return String(value)
  }

  // Check if two values are different
  const isDifferent = (oldVal: any, newVal: any): boolean => {
    if (oldVal === newVal) return false
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) return false
    // Handle empty values
    const oldEmpty = oldVal === null || oldVal === undefined || oldVal === ''
    const newEmpty = newVal === null || newVal === undefined || newVal === ''
    if (oldEmpty && newEmpty) return false
    return true
  }

  // Render a diff row for a single field
  const renderDiffRow = (
    label: string,
    oldValue: any,
    newValue: any,
    key: string
  ) => {
    const changed = isDifferent(oldValue, newValue)
    
    return (
      <Box
        key={key}
        sx={{
          display: 'grid',
          gridTemplateColumns: '180px 1fr 40px 1fr',
          gap: 2,
          py: 1.5,
          px: 2,
          bgcolor: changed ? 'warning.50' : 'transparent',
          borderBottom: '1px solid',
          borderColor: 'divider',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: changed ? 'text.disabled' : 'text.primary',
            textDecoration: changed ? 'line-through' : 'none',
            wordBreak: 'break-word',
          }}
        >
          {renderValue(oldValue)}
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          {changed && (
            <ArrowForwardIcon fontSize="small" color="primary" />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: changed ? 'success.main' : 'text.primary',
            fontWeight: changed ? 600 : 400,
            wordBreak: 'break-word',
          }}
        >
          {renderValue(newValue)}
        </Typography>
      </Box>
    )
  }

  // Render the profile diff section
  const renderProfileDiff = () => {
    const currentProfile = currentData?.profile_data || {}
    const submittedProfile = selectedSubmission?.submitted_profile_data || {}
    const allKeys = Array.from(new Set([...Object.keys(currentProfile), ...Object.keys(submittedProfile)]))

    const hasChanges = allKeys.some(key => isDifferent(currentProfile[key], submittedProfile[key]))

    if (!hasChanges) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ingen ændringer i profildata
        </Alert>
      )
    }

    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '180px 1fr 40px 1fr',
              gap: 2,
              py: 1.5,
              px: 2,
              bgcolor: 'grey.100',
              borderBottom: '2px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2">Felt</Typography>
            <Typography variant="subtitle2">Nuværende</Typography>
            <Box />
            <Typography variant="subtitle2">Ny værdi</Typography>
          </Box>

          {/* Rows */}
          {allKeys
            .filter(key => PROFILE_FIELD_LABELS[key]) // Only show known fields
            .map(key => renderDiffRow(
              PROFILE_FIELD_LABELS[key] || key,
              currentProfile[key],
              submittedProfile[key],
              `profile-${key}`
            ))
          }
        </CardContent>
      </Card>
    )
  }

  // Render the services diff section
  const renderServicesDiff = () => {
    const currentServices = currentData?.services_data?.data || {}
    const submittedServices = selectedSubmission?.submitted_services_data?.data || {}
    const template = selectedSubmission?.template_structure

    if (!template?.sections) {
      return (
        <Alert severity="warning">
          Kunne ikke indlæse services skabelon
        </Alert>
      )
    }

    return (
      <Box>
        {template.sections.map(section => {
          const sectionHasChanges = section.fields.some(field =>
            isDifferent(currentServices[field.id], submittedServices[field.id])
          )

          return (
            <Accordion
              key={section.id}
              defaultExpanded={sectionHasChanges}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                  {sectionHasChanges && (
                    <Chip label="Ændret" size="small" color="warning" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {/* Header */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '180px 1fr 40px 1fr',
                    gap: 2,
                    py: 1.5,
                    px: 2,
                    bgcolor: 'grey.100',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2">Felt</Typography>
                  <Typography variant="subtitle2">Nuværende</Typography>
                  <Box />
                  <Typography variant="subtitle2">Ny værdi</Typography>
                </Box>

                {/* Field rows */}
                {section.fields.map(field =>
                  renderDiffRow(
                    field.label,
                    currentServices[field.id],
                    submittedServices[field.id],
                    `service-${field.id}`
                  )
                )}
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Gennemse indsendelser
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Godkend eller afvis indsendte data fra bedemænd
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchSubmissions}
          disabled={loading}
        >
          Opdater
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Empty state */}
      {submissions.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Ingen afventende indsendelser
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Når bedemænd indsender deres oplysninger, vil de vises her til gennemgang.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Bedemand</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sidst opdateret</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Handlinger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow
                  key={submission.id}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {submission.entity_name}
                    </Typography>
                    {submission.entity_city && (
                      <Typography variant="caption" color="text.secondary">
                        {submission.entity_city}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={submission.entity_type === 'chain' ? 'Kæde' : 'Bedemand'}
                      size="small"
                      color={submission.entity_type === 'chain' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {submission.submission_type === 'complete' ? (
                      <Chip
                        label="Komplet"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Tooltip title="Kun services/priser udfyldt (side 1). Profildata ikke udfyldt.">
                        <Chip
                          label="Delvis (kun side 1)"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(submission.submitted_at || submission.token_created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Gennemse og godkend/afvis">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenReview(submission)}
                      >
                        Gennemse
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => !processing && setReviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Gennemse indsendelse
                </Typography>
                {selectedSubmission?.submission_type === 'partial' && (
                  <Chip label="Delvis" size="small" color="warning" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {selectedSubmission?.entity_name} • {selectedSubmission?.submitted_at 
                  ? `Indsendt ${formatDate(selectedSubmission.submitted_at)}`
                  : `Påbegyndt ${formatDate(selectedSubmission?.token_created_at || '')}`
                }
              </Typography>
            </Box>
            <IconButton
              onClick={() => setReviewDialogOpen(false)}
              disabled={processing}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loadingCurrentData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Partial submission warning */}
              {selectedSubmission?.submission_type === 'partial' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <strong>Delvis indsendelse:</strong> Denne bedemand har kun udfyldt side 1 (services/priser). 
                  Profildata er ikke ændret.
                </Alert>
              )}

              {/* Services section - show first since it's the important one for partials */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Services & Priser
              </Typography>
              {renderServicesDiff()}

              <Divider sx={{ my: 3 }} />

              {/* Profile section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Profildata
                {selectedSubmission?.submission_type === 'partial' && (
                  <Chip label="Ikke udfyldt" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              {renderProfileDiff()}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setReviewDialogOpen(false)}
            disabled={processing}
          >
            Luk
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleOpenReject}
            disabled={processing || loadingCurrentData}
          >
            Afvis
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            onClick={handleApprove}
            disabled={processing || loadingCurrentData}
          >
            {processing ? 'Godkender...' : 'Godkend og anvend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !processing && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Afvis indsendelse</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Du kan valgfrit angive en årsag til afvisningen (kun til intern brug).
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Årsag (valgfrit)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="F.eks. 'Mangler priser på alle pakker' eller 'Kontaktoplysninger er ufuldstændige'"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processing}>
            Annuller
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CloseIcon />}
          >
            {processing ? 'Afviser...' : 'Afvis'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

